import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Flame, LayoutDashboard, Sliders, MessageSquare, CreditCard, ShieldCheck, LogOut, Rocket, UserCog, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const NAV_ITEMS = [
  { to: "/guia", label: "Guia Rápido", icon: Rocket, testid: "nav-guia-link" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
  { to: "/aquecimento", label: "Aquecimento", icon: Sliders, testid: "nav-aquecimento-link" },
  { to: "/planos", label: "Planos", icon: CreditCard, testid: "nav-planos-link" },
  { to: "/conta", label: "Minha Conta", icon: UserCog, testid: "nav-conta-link" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success("Sessão encerrada");
    navigate("/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 whitespace-nowrap ${
      isActive
        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
        : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent"
    }`;

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100 flex">
      <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-slate-800/80 bg-[#0B111E] p-5 fixed inset-y-0 left-0 z-30">
        <div className="flex items-center gap-2.5 px-1 mb-8">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Flame className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <span className="font-heading font-extrabold text-lg tracking-tight">MaturaMax</span>
            <span className="block text-[10px] text-slate-500 -mt-0.5">Aquecimento de chips 24/7</span>
          </div>
        </div>
        <nav className="flex flex-col gap-1.5 flex-1">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} data-testid={item.testid}>
              <item.icon className="w-4.5 h-4.5 w-[18px] h-[18px]" />
              {item.label}
            </NavLink>
          ))}
          {user && user.role === "admin" && (
            <>
              <NavLink to="/frases" className={linkClass} data-testid="nav-frases-link">
                <MessageSquare className="w-[18px] h-[18px]" />
                Banco de Frases
              </NavLink>
              <NavLink to="/admin/funil" className={linkClass} data-testid="nav-funil-link">
                <TrendingUp className="w-[18px] h-[18px]" />
                Funil de Conversão
              </NavLink>
              <NavLink to="/admin" className={linkClass} data-testid="nav-admin-link">
                <ShieldCheck className="w-[18px] h-[18px]" />
                Administração
              </NavLink>
            </>
          )}
        </nav>
        <div className="border-t border-slate-800/80 pt-4 mt-4">
          <div className="px-1 mb-3">
            <p className="text-sm font-semibold text-slate-200 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="auth-logout-button"
            className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-red-300 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-colors duration-150 w-full"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Sair da conta
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-[#0B111E]/95 backdrop-blur-md border-b border-slate-800/80">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-emerald-400" />
            <span className="font-heading font-extrabold text-lg">MaturaMax</span>
          </div>
          <button onClick={handleLogout} data-testid="auth-logout-button-mobile" className="text-slate-400 hover:text-red-300 p-2">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex gap-1.5 overflow-x-auto px-3 pb-2.5">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} data-testid={`${item.testid}-mobile`}>
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          {user && user.role === "admin" && (
            <>
              <NavLink to="/frases" className={linkClass} data-testid="nav-frases-link-mobile">
                <MessageSquare className="w-4 h-4" />
                Frases
              </NavLink>
              <NavLink to="/admin/funil" className={linkClass} data-testid="nav-funil-link-mobile">
                <TrendingUp className="w-4 h-4" />
                Funil
              </NavLink>
              <NavLink to="/admin" className={linkClass} data-testid="nav-admin-link-mobile">
                <ShieldCheck className="w-4 h-4" />
                Admin
              </NavLink>
            </>
          )}
        </nav>
      </div>

      <main className="flex-1 md:ml-64 pt-28 md:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
